import HotTable from '@handsontable/react';
import { Stack } from '@mui/material';
import { useSnackbar } from 'notistack5';
import React, { RefObject, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GeneralEnquete } from 'src/@types/generalEnquete';
import { StyledButton } from 'src/components/app-components/StyledButton';
import { StyledLoading } from 'src/components/app-components/StyledLoading';
import { ANSWER_STATUS, AnswerStatus } from 'src/constants';
import { useTableAnswerSave } from 'src/features/general/enquete-answer/hooks/useTableAnswerSave';
import useAuth from 'src/hooks/useAuth';
import { usePermission } from 'src/hooks/usePermission';
import { useAppSelector } from 'src/redux/store';
import useEnqueteAnswerSave from '../hooks/useEnqueteAnswerSave';
import { SwitchReversionModalLayout } from './common/components/SwitchReversionModalLayout';
import { REVERSION, REVERSION_REQUEST } from './common/constants';
import { useReturnedHandleClick } from './common/hooks/useReturnedHandleClick';
export interface EnqueteAnswerSaveButtonProps {
  respondentTargetId: string;
  confirmRequestId: string | null;
  answerHotRef: RefObject<HotTable>[];
  answerHotRefForSubSheet: RefObject<HotTable>[];
}

const EnqueteAnswerGeneralTableSaveButton: React.FC<EnqueteAnswerSaveButtonProps> = (props): React.ReactElement => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const { isSaving, isAnswered, commonSaveHandler } = useEnqueteAnswerSave(props.respondentTargetId);
  const { saveAllSettings } = useTableAnswerSave(props.answerHotRef, props.answerHotRefForSubSheet);
  const confirmRequestData = useAppSelector((state) => state.enqueteAnswer.confirmRequestData);
  const isEnqueteAnswerOpen = useAppSelector((state) => state.enqueteAnswer.isEnqueteAnswerOpen);
  const enqueteAnswerData = useAppSelector((state) => state.enqueteAnswer.enqueteAnswerData);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { handleReversionType, isReversionRequest } = useReturnedHandleClick(setIsOpen);
  const userId = null !== user ? user.email : '';
  const respondentRequestData = useAppSelector((state) => state.enqueteAnswer.respondentRequestData);
  const canRevert = respondentRequestData.canRevert;
  const { isAdmin, isViewerAdmin, isSurveyOwner, isRespondentPerson, isAnswerEditor, isConfirmor } = usePermission();
  // NOTE: 編集可能（提出・一時保存）なユーザーの権限・ロール
  const canEdit =
    isAdmin ||
    isSurveyOwner ||
    isRespondentPerson ||
    isAnswerEditor ||
    (isConfirmor && enqueteAnswerData.status === ANSWER_STATUS.ANSWERED && !canRevert);
  const marginTopValue = props.confirmRequestId && confirmRequestData.confirmerId === userId ? '20px' : '50px';

  const submitHandler = async (answerStatus: AnswerStatus, isSubmit: boolean) => {
    const tableSettings = saveAllSettings();

    // 入力チェック
    let isValid = true;
    // 提出・再提出時に、入力チェックを行う
    if (isSubmit) {
      // メイン表の無効値チェック
      isValid = tableSettings.cell.every((cell) => cell.valid !== false);
      // 拡張用回答表の無効値チェック
      if (isValid) {
        isValid = tableSettings.additionalTables.every((table) => table.cell.every((cell) => cell.valid !== false));
      }
    }

    if (!isValid) {
      // 入力チェックNGの場合、エラーメッセージを表示
      enqueueSnackbar(t('message.tableInvalid'), { variant: 'error' });
    } else {
      await commonSaveHandler({ tables: tableSettings } as GeneralEnquete, answerStatus);
    }
  };

  return (
    <>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 2, md: 4 }} sx={{ marginTop: marginTopValue }}>
        {(isSurveyOwner || isAdmin || isViewerAdmin) && canRevert && (
          <StyledButton
            onClick={() => handleReversionType(REVERSION)}
            isDisabled={!isEnqueteAnswerOpen || enqueteAnswerData.status !== ANSWER_STATUS.ANSWERED || !(isSurveyOwner || isAdmin)}>
            {t('button.reversion')}
          </StyledButton>
        )}
        {(isRespondentPerson || isAdmin || isViewerAdmin) && canRevert && (
          <StyledButton
            onClick={() => handleReversionType(REVERSION_REQUEST)}
            isDisabled={!isEnqueteAnswerOpen || enqueteAnswerData.status !== ANSWER_STATUS.ANSWERED || !(isRespondentPerson || isAdmin)}>
            {t('button.requestReversion')}
          </StyledButton>
        )}
        <StyledButton
          onClick={() => submitHandler(enqueteAnswerData.status === ANSWER_STATUS.RETURNED ? ANSWER_STATUS.RETURNED : ANSWER_STATUS.SAVED, false)}
          isDisabled={isAnswered || !canEdit}>
          {t('button.temporarilySave')}
        </StyledButton>
        <StyledButton
          onClick={() => submitHandler(ANSWER_STATUS.ANSWERED, true)}
          isDisabled={!isEnqueteAnswerOpen || (enqueteAnswerData.status === ANSWER_STATUS.ANSWERED && canRevert) || !canEdit}>
          {t(enqueteAnswerData.status === ANSWER_STATUS.RETURNED ? 'button.resubmission' : 'button.submit')}
        </StyledButton>
      </Stack>
      <StyledLoading isOpen={isSaving} />
      <SwitchReversionModalLayout isOpen={isOpen} setIsOpen={setIsOpen} isRequest={isReversionRequest} />
    </>
  );
};

export default EnqueteAnswerGeneralTableSaveButton;
